import React, { useEffect, useRef } from 'react';

import {
    Comment,
    Comments,
    GetCommentsInput,
    GetCommentsOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { contractConfigurationClient } from 'src/api/client';
import { AppContext } from 'src/contexts/appContext';

import { CommentForm } from '../CommentForm';

interface ICommentListProps {
    category: string;
    categoryUniqueColumnId: number;
    comments: Comments;
    setComments: (comments: Comments) => void;
    searchQuery?: string;
    showEmptyState?: boolean;
    emptyStateMessage?: string;
    noResultsMessage?: string;
    onCommentsLoaded?: (comments: Comments) => void;
}

export const CommentList = ({
    category,
    categoryUniqueColumnId,
    comments,
    setComments,
    searchQuery = '',
    showEmptyState = true,
    emptyStateMessage = 'No comments yet',
    noResultsMessage = 'No comments match your search',
    onCommentsLoaded
}: ICommentListProps) => {
    const { setTopAlertMsg } = React.useContext(AppContext);
    const commentListRef = useRef<HTMLDivElement>(null);

    // Load comments on initial mount
    useEffect(() => {
        (contractConfigurationClient
            .getComments(
                GetCommentsInput.clone({
                    category: category,
                    categoryUniqueColumnId: categoryUniqueColumnId
                })
            )
            .subscribe((response: GetCommentsOutput) => {
                if (response.comments) {
                    setComments(
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        response.comments.sort((a, b) => b.creationDate!.getTime() - a.creationDate!.getTime())
                    );
                    if (onCommentsLoaded) {
                        onCommentsLoaded(response.comments);
                    }
                    if (commentListRef.current) commentListRef.current.scrollTop = 0;
                }
            }),
            (error: UnknownCoralError) => {
                console.error('GetComments Unexpected error', error.receivedException.message);
                setTopAlertMsg(error.receivedException.message);
            });
    }, [categoryUniqueColumnId, category]);

    // Filter comments based on search query
    const filteredComments = React.useMemo(() => {
        if (!searchQuery.trim()) {
            return comments;
        }

        const query = searchQuery.toLowerCase();
        return comments.filter((comment) => {
            return comment.comment?.toLowerCase().includes(query);
        });
    }, [searchQuery, comments]);

    const deleteCommentStateHandler = (commentId: number) => {
        setComments(comments.filter((commentObj) => commentObj.commentId !== commentId));
    };

    const updateCommentModifiedStateHandler = (
        updatedComment: string,
        commentId: number,
        creationDate: Date,
        newCommentId: number
    ) => {
        setComments(
            comments.map((commentObj: Comment) =>
                commentObj.commentId === commentId
                    ? { ...commentObj, comment: updatedComment, creationDate: creationDate, commentId: newCommentId }
                    : commentObj
            )
        );
    };

    return (
        <div ref={commentListRef}>
            {filteredComments.length > 0 ? (
                <Row width="100%" widths={['100%']}>
                    <Column>
                        {filteredComments.map((comment, index) => (
                            <CommentForm
                                key={index}
                                updateCommentModifiedStateHandler={updateCommentModifiedStateHandler}
                                deleteCommentStateHandler={deleteCommentStateHandler}
                                {...comment}
                            />
                        ))}
                    </Column>
                </Row>
            ) : searchQuery.trim() ? (
                <Column alignmentHorizontal="center" spacingInset="600">
                    <Text type="h200">{noResultsMessage}</Text>
                    <Text type="b200" color="secondary">
                        Try adjusting your search terms
                    </Text>
                </Column>
            ) : showEmptyState ? (
                <Column alignmentHorizontal="center" spacingInset="600">
                    <Text type="h200">{emptyStateMessage}</Text>
                </Column>
            ) : null}
        </div>
    );
};
